import React from 'react';
import { observer } from 'mobx-react';
import { CollapseMenu } from '../../common/CollapseMenu/CollapseMenu';
import './details.scss';
import { LinkComp } from 'cd-eft/components/common/Links/extLink';

export const PopupContainer = (props: { title?: String }) => {
    if (props.title === undefined) {
        return (
            <div className="waterway-sizing-calculate-details">
                <CollapseMenu
                    titleClass={'waterway-sizing-calculate-detail-menu'}
                    noIcon={true}
                    title={'Soil strength '}
                    isOpen={true}
                >
                    <SoilStrengthDetail className="ml-3 mr-3" />
                </CollapseMenu>
                <CollapseMenu
                    titleClass={'waterway-sizing-calculate-detail-menu'}
                    noIcon={true}
                    title={'Vegetal cover'}
                    isOpen={true}
                >
                    <VegetalCoverDetail className="ml-3 mr-3 small-rci-table" />
                </CollapseMenu>
                <CollapseMenu
                    titleClass={'waterway-sizing-calculate-detail-menu'}
                    noIcon={true}
                    title={'Channel flow'}
                    isOpen={true}
                >
                    <ChannelFlowDetail className="ml-3 mr-3" />
                </CollapseMenu>
            </div>
        );
    }
    return (
        <div>
            {props.title === 'Define Soil Strength' && <SoilStrengthDetail />}
            {props.title === 'Define Vegetal Cover' && <VegetalCoverDetail className="wide-rci-table" />}
            {props.title === 'Define Channel & Flow' && <ChannelFlowDetail />}
        </div>
    );
};

export const WaterWaySizingCalculatorDetails = observer(PopupContainer);

export const SoilStrengthDetail = (props: { className?: string }) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                Soil strength is the ability of the soil(s) within the waterway channel to withstand hydraulic stresses created
                by storm flows. It’s expressed as allowable stress and is determined from specific physical properties.
            </p>
            <p>
                <strong>Allowable Soil Stress</strong>is defined one of three ways:
                <br />
                <ol>
                    <li>
                        <u>Implied</u> based on standard soil <strong>Erodibility</strong> classifications:
                        <ol type="a">
                            <li>Easily eroded (Weak sandy soils)</li>

                            <li>Erodible (Silt textural class with low plasticity on order of 10)</li>

                            <li>Erosion Resistant (Clay textural class with plasticity on order of 15)</li>

                            <li>Very Erosion Resistant (Gravel textural class and fat clays with high plasticity)</li>
                        </ol>
                    </li>
                    <li>
                        <u>Calculated</u> from <strong>Soil Parameters</strong>/Properties

                        <ol type="a">
                            <li>The Unified Soil Classification (USCS) name/symbol is required:
                                <ol type="i">
                                    <li>GW, GP, SW, and SP are non-cohesive soils.</li>

                                    <li>All others listed are considered cohesive soils.</li>
                                </ol>
                            </li>
                            <li>For non-cohesive soils, the USCS and soil grain size roughness are required.
                                <ol type="i">
                                    <li>
                                        The d<sub>75</sub> grain size may be estimated from the soil survey or determined by laboratory
                                        analysis.
                                    </li>
                                </ol>
                            </li>
                            <li>
                                For cohesive soils, the USCS, plasticity index, and void ratio are required. The d<sub>75</sub> grain size
                                roughness value is <u>always</u> 0.0156 (d<sub>75</sub> &le; 0.05 inches, &le; 1.25 mm)
                            </li>
                            <li>Plasticity Index (PI)
                                <ol type="i">
                                    <li>Typically, between 10 and 24.</li>

                                    <li>Available from the soil survey.</li>

                                    <li>Determined via laboratory procedures according to ASTM D-4318.</li>
                                </ol>
                            </li>
                            <li>Void ratio
                                <ol type="i">
                                    <li>The ratio of the volume of voids (water and air) to the volume of solid particles</li>

                                    <li>Expressed as a decimal and may exceed 1.</li>

                                    <li>May be estimated based on soil type or by using standard laboratory procedures.</li>
                                </ol>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <strong>Soil Stress</strong> <u>directly entered</u> by the user (if known or for unique design comparison
                        purposes)
                    </li>
                </ol>
            </p>
            <p>
                <span>
                    <LinkComp 
                        label='NEH Part 650 Engineering Field Handbook, Chapter 4, Elementary Soils Engineering (usda.gov)'
                        labelClass='https://directives.sc.egov.usda.gov/OpenNonWebContent.aspx?content=46259.wba'
                        id={'soils-link-1'}
                    />
                    <LinkComp 
                        label='US Bureau of Reclamation Engineering Geology Field Manual Volume 1 Chapter 3'
                        labelClass='https://www.usbr.gov/tsc/techreferences/mands/geologyfieldmanual-vol1/chap03.pdf'
                        id={'soils-link-2'}
                    />
                    <LinkComp 
                        label='NEH Part 650 Engineering Field Handbook, Chapter 7, Grassed Waterways (usda.gov)'
                        labelClass='https://directives.sc.egov.usda.gov/OpenNonWebContent.aspx?content=46771.wba'
                        id={'soils-link-3'}
                    />
                </span>
            </p>
        </div>
    );
};

export const VegetalCoverDetail = (props: { className?: string }) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                Allowable vegetal stress is the maximum total shear stress that may be withstood by the vegetal cover without
                unacceptable damage. It is directly related to the drag experienced by the stems and therefore to the level of
                flow retardance offered by the vegetal cover.
            </p>
            <p>
                The{' '}
                <strong>
                    Vegetal Cover Factor, C<sub>F</sub>
                </strong>
                , accounts for the cover density and uniformity. It describes the ability of the vegetal cover to reduce the
                effective hydraulic stress on the soil.
                <br />
                <br />
                Select Vegetal Cover Type (species chosen) to represent the sparsest expected cover.{' '}
                <strong>Cover Type Description</strong> and <strong>Value</strong> can be manually entered. The vegetal cover
                factor ranges from 0 (no vegetal protection) to 1 (completely protected from stress).
                <br />
                <br />
                Allowable vegetal stress is related to the{' '}
                <strong>
                    Retardance Curve Index, C<sub>I</sub>
                </strong>
                , which is in turn related to the <strong>Stem Length and Density</strong> of the cover. When entering stem
                length and density directly, use a conservative but realistic mowed/maintained length (e.g., 3"-4" for stability
                and 12"-18" for capacity).
                <br />
                <br />
                The value of C<sub>I</sub> can also be determined using the <strong>Retardance Classes</strong>:
                <br />
                <table className="rci-table">
                    <tr>
                        <td>
                            <strong>NRCS Retardance Class</strong>
                        </td>
                        <td>
                            <strong>
                                Retardance Curve Index, C<sub>I</sub>
                            </strong>
                        </td>
                    </tr>
                    <tr>
                        <td>A</td>
                        <td>10.0</td>
                    </tr>
                    <tr>
                        <td>B</td>
                        <td>7.64</td>
                    </tr>
                    <tr>
                        <td>C</td>
                        <td>5.60</td>
                    </tr>
                    <tr>
                        <td>D</td>
                        <td>4.44</td>
                    </tr>
                    <tr>
                        <td>E</td>
                        <td>2.88</td>
                    </tr>
                </table>
                Retardance class D is generally used for stability (shortest and sparsest cover) and B or C are used for
                capacity (longest and densest cover).
                <br />
                <br />
                The cover factor, C<sub>F</sub>, is a variable in the <u>actual effective</u> stress calculation that
                represents the vegetal cover’s ability to reduce the hydraulic stresses on the <u>soil</u>, while the
                retardance curve index, C<sub>I</sub>, is used to calculate the maximum allowable hydraulic stresses on the{' '}
                <u>vegetation</u>.
                <br />
                <br />
                For most soils encountered in practice, soil particles will be detached before damage to vegetation occurs;
                maximum allowable vegetal stress will rarely dictate design.
                <br />
                <br />
                <strong>Manning's "n"</strong> value can also be directly entered. Using an “n” value bypasses maximum allowable
                vegetal stress calculations, ensuring that channel dimensions result in the actual effective soil stress being
                equal to or less than the allowable soil stress.
            </p>
        </div>
    );
};

export const ChannelFlowDetail = (props: { className?: string }) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The channel will be either <strong>Trapezoidal</strong> or <strong>Parabolic</strong> in shape. To design a V-Channel, set the trapezoidal bottom
                width to 0.1 feet.
            </p>
            <p>
                <strong>Trapezoidal Controls</strong>:
                <br />
                <strong>Left Slope</strong> and <strong>Right Slope</strong> are the side slopes of the channel expressed in
                ft/ft (run:rise slope ratio) and do not need to be equal.
                <br />
                <strong>Minimum Bed Width</strong> is the minimum bottom width of a trapezoidal channel. Effective stress calculations may create
                wider bed widths as needed.
            </p>
            <p>
                <strong>Parabolic Controls</strong>:
                <br />
                <strong>Limiting Slope</strong> defines steepest side slope allowed as the tangent to the parabolic curve extends straight to
                original ground.
                <br />
                <strong>Offset 1,2,3</strong> values are optional and will define depth values referenced from the channel top
                (at flow depth, plus freeboard if applicable) at the distance percentages set by the user (e.g., a waterway with
                a top width of 40 feet and offsets of 25, 75 and 100% will create depths at distances of <i>.25 x 20’ = 5’</i>,{' '}
                <i>.75 x 20’ = 15’</i>, and <i>1.0 x 20’ = 20’</i> from the channel centerline).
            </p>
            <p>
                <strong>Applicable to Both Channel Shapes</strong>:
                <br />
                If extra channel depth is needed above the flow depth, enter the desired <strong>Freeboard</strong> value.
            </p>
            <p>
                <strong>Channel Options</strong>:
                <br />
                Choose <strong>Not User Defined</strong> to let the calculator determine both the required flow depth and flow
                width to satisfy allowable stress design criteria.
                <br />
                Choose <strong>Fix Flow Depth</strong> to force the calculator to use a user-defined flow depth when determining
                the required flow width to satisfy allowable stress design criteria.
                <br />
                Choose <strong>Fix Flow Width</strong> to force the calculator to use a user-defined flow width when determining
                the required flow depth to satisfy allowable stress design criteria.
                <br />
                Note: Fixing both is not currently allowed.
            </p>
        </div>
    );
};
